import ShowableController from '../utilities/showableController';
import { stateable } from './stateable';
import { isMediatable, isCatalogItem } from '../utilities/guards';
import CatalogItem from '../core/catalogItem';
import Logger from '../utilities/logger';
import Injector, { InjectorScope } from '../core/injector';
/** The name of the module */
export const name = 'Showable';
/** Class names for the Showables */
export var ClassNames;
(function (ClassNames) {
    ClassNames["Default"] = "df-u-showable";
    ClassNames["Visible"] = "df-u-showable--visible";
})(ClassNames || (ClassNames = {}));
/** Mediated events triggered by this module */
export var MediatedEvents;
(function (MediatedEvents) {
    MediatedEvents["ShowRequest"] = "showRequest";
    MediatedEvents["Show"] = "show";
    MediatedEvents["Shown"] = "shown";
    MediatedEvents["HideRequest"] = "hideRequest";
    MediatedEvents["Hide"] = "hide";
    MediatedEvents["Hidden"] = "hidden";
})(MediatedEvents || (MediatedEvents = {}));
/** A single instance of ShowableController */
let controller;
// eslint-disable-next-line @typescript-eslint/ban-types
export const showable = (overlayInjectionKey) => (targetClass) => {
    const { prototype } = targetClass;
    const showOverlay = typeof overlayInjectionKey !== 'undefined';
    // Make sure the ShowableController instance exists and that an Overlay object was passed in
    if (typeof controller === 'undefined') {
        controller = new ShowableController();
    }
    if (typeof overlayInjectionKey !== 'undefined') {
        if (Injector.isDefinedAsSingleton(overlayInjectionKey)) {
            const overlay = Injector.get(overlayInjectionKey, InjectorScope.Singleton);
            controller.setOverlay(overlay);
        }
        else {
            Logger.error('OverlayInterface module provided is not properly configured as a singleton instance');
        }
    }
    // Manually make the object stateable
    stateable(targetClass.name);
    /**
     * Helper function for mediating custom events
     *
     * @param obj Showable object to mediate event for
     * @param eventName Name of the event (e.g. 'show')
     * @param eventData Custom data to be included with the event
     */
    const mediate = (obj, eventName, eventData = {}) => {
        // Check if the provided object is mediatable and a catalog item
        if (isMediatable(obj) && isCatalogItem(obj)) {
            // Trigger the mediation and include the element triggering the event
            obj.mediate(eventName, {
                ...eventData,
                element: obj.getElement(),
            });
        }
    };
    /**
     * Show the Showable element
     */
    prototype.show = async function show(showOverlayOverride = false) {
        try {
            // Make sure the init queue has completed
            await this.isInitialized();
        }
        catch {
            Logger.error('Showable initialization failed');
        }
        // Mediate the show request event
        mediate(this, MediatedEvents.ShowRequest);
        // Request the element to be shown by the ShowableController
        return controller.requestShow(this._showableId, typeof showOverlayOverride === 'boolean' ? showOverlayOverride : showOverlay); // eslint-disable-line no-underscore-dangle
    };
    /**
     * Hide the Showable element
     */
    prototype.hide = async function hide() {
        // Mediate the hide request event
        mediate(this, MediatedEvents.HideRequest);
        // Request the element to be shown by the ShowableController
        return controller.requestHide(this._showableId); // eslint-disable-line no-underscore-dangle
    };
    /**
     * Check if Showable element is currently visible
     */
    prototype.isVisible = function isVisible() {
        return this.getState().isVisible;
    };
    /**
     * Perform the actual showing of the Showable element
     */
    prototype._show = async function _show() {
        // Make sure state is initialized
        if (typeof this.setState === 'undefined') {
            throw new Error('State not yet initialized');
        }
        // Call preshow hook
        if (this.preshow) {
            this.preshow();
        }
        // Mediate the show event
        mediate(this, MediatedEvents.Show);
        // Update the state to indicate the Showable is visible
        this.setState({
            isVisible: true,
        });
        // Wait until the element is available in the DOM
        await this.isElementAvailable();
        // Add class being used to show element
        this.element.classList.add(ClassNames.Visible);
        // Mediate the shown event
        mediate(this, MediatedEvents.Shown);
    };
    /**
     * Perform the actual hiding of the Showable element
     */
    prototype._hide = function _hide() {
        // Make sure state is initialized
        if (typeof this.setState === 'undefined') {
            throw new Error('State not yet initialized');
        }
        // Mediate the hide event
        mediate(this, MediatedEvents.Hide);
        // Call prehide hook
        if (this.prehide) {
            this.prehide();
        }
        // Update the state to indicate the Showable is not visible
        this.setState({
            isVisible: false,
        });
        // Remove class being used to show element
        this.element.classList.remove(ClassNames.Visible);
        // Mediate the hidden event
        mediate(this, MediatedEvents.Hidden);
    };
    // Register the object with the ShowableController
    prototype._initShowable = function _initShowable() {
        this._showableId = controller.register(this, this.triggerElement || null); // eslint-disable-line no-underscore-dangle
    };
    CatalogItem.addToInitQueue(targetClass.name, prototype._initShowable); // eslint-disable-line no-underscore-dangle
    return targetClass;
};
